import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { API } from "@gui/infrastructure/pass-through/api";
import { UserContext } from "@gui/contexts/user/UserContext";
import { ALL_ADMIN_LINKS, DEVELOPER_LINKS, USER_LINKS } from "@gui/widgets/Toolbar";

/**
 * Maps each path that has an "issues" count list to the API call that returns the array of
 * jobs/issues in that bucket. Source of truth for which routes have a count badge; the
 * Provider fetches each entry whose path is visible to the current user once at mount.
 */
const FETCHERS: Record<string, () => Promise<unknown[]>> = {
	"/admin/deal-matcher": () => API.Data.Jobs.issues.getDealMatcherJobs(),
	"/admin/branch-matcher": () => API.Data.Jobs.issues.getBranchMatcherJobs(),
	"/admin/agrian-issues": () => API.Data.Jobs.issues.getAgrian(),
	"/admin/exe-mission-validation": () => API.Data.Jobs.issues.getExeMissionValidationJobs(),
	"/admin/job-errors": () => API.Data.Jobs.issues.getAllIssueJobs(),
	"/admin/data-sendback-issues": () => API.Data.Jobs.issues.getDataSendback(),
};

/** Set of paths that have a count fetcher — used by sidebar / Home to know which links should
 *  render a badge slot at all. */
export const ISSUE_COUNT_PATHS: ReadonlySet<string> = new Set(Object.keys(FETCHERS));

export interface IssueCountsValue {
	/** Current cached counts keyed by route path. `undefined` means "not yet fetched / not
	 *  visible to this user". A value of `0` means "fetched, no issues". */
	counts: Record<string, number>;
	/** True while the initial fan-out fetch is in flight. False once all fetchers have settled. */
	loading: boolean;
	/** Live-update a count from a page that just fetched its own list. */
	setCount: (path: string, count: number) => void;
}

/**
 * Owns issue-count state and the fan-out fetch. Runs once on mount for the
 * subset of FETCHERS visible to the current user. Live updates come via
 * `setCount` from page-level vms after their own fetch.
 *
 * Extracted out of `IssueCountsContext.tsx` so the Context provider can stay
 * a presentational shell — all API calls + effects live here.
 */
export function useIssueCountsState(): IssueCountsValue {
	const user = useContext(UserContext);
	const [counts, setCounts] = useState<Record<string, number>>({});
	const [loading, setLoading] = useState(false);
	const startedRef = useRef(false);

	const visiblePaths = useMemo(() => {
		const paths = new Set(USER_LINKS.map((l) => l.to));
		if (user?.isAdmin) ALL_ADMIN_LINKS.forEach((l) => paths.add(l.to));
		if (user?.isDeveloper) DEVELOPER_LINKS.forEach((l) => paths.add(l.to));
		return paths;
	}, [user?.isAdmin, user?.isDeveloper]);

	useEffect(() => {
		if (!user) return;
		// Dedup against StrictMode double-invoke; the cache lives for the page lifetime.
		if (startedRef.current) return;
		const entries = Object.entries(FETCHERS).filter(([path]) => visiblePaths.has(path));
		if (entries.length === 0) return;
		startedRef.current = true;
		setLoading(true);
		Promise.allSettled(entries.map(([, fetch]) => fetch())).then((results) => {
			const next: Record<string, number> = {};
			results.forEach((r, i) => {
				if (r.status === "fulfilled") next[entries[i][0]] = r.value.length;
			});
			setCounts((prev) => ({ ...prev, ...next }));
			setLoading(false);
		});
	}, [user, visiblePaths]);

	const setCount = useCallback((path: string, count: number) => {
		setCounts((prev) => (prev[path] === count ? prev : { ...prev, [path]: count }));
	}, []);

	return useMemo(() => ({ counts, loading, setCount }), [counts, loading, setCount]);
}
